import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { ExternalLink } from "@/components/Button/ExternalLink";
import AppleLogo from "@/components/icons/AppleLogo";
import { getEnvVar } from "@/common/utils/environment";
import { compareClientVersions } from "@/common/utils/clientVersion";
import packageJson from "../../package.json";
import { useEffect, useState } from "react";

type ClientVersionConfig = {
  minimumClientVersions?: {
    ios: string | null;
  };
  appStoreUrl?: string;
};

type VersionGateState =
  | { status: "allowed" }
  | { status: "blocked"; appStoreUrl: string; minimumVersion: string };

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const PRODUCTION_IOS_BUNDLE_ID = "com.saileapp.saile";
const DEFAULT_APP_STORE_URL =
  "https://apps.apple.com/us/app/saile-medical/id6738873421";

export function ForceAppUpdateGate({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<VersionGateState>({ status: "allowed" });

  useEffect(() => {
    if (Capacitor.getPlatform() !== "ios" || !Capacitor.isNativePlatform()) {
      return;
    }

    let isMounted = true;

    const checkVersion = async () => {
      try {
        const appInfo = await App.getInfo();

        // The QA and Local Xcode targets ship as version 1.0 under their own
        // bundle IDs, so any real minimum would lock them out and send testers
        // to the production App Store listing. Only the production app is gated.
        if (appInfo.id !== PRODUCTION_IOS_BUNDLE_ID) {
          return;
        }

        const configResponse = await fetch(
          `${getEnvVar("VITE_PUBLIC_API_HOST")}/app-config/client-version`,
        );

        if (!configResponse.ok) {
          return;
        }

        const config = (await configResponse.json()) as ClientVersionConfig;
        const minimumVersion = config.minimumClientVersions?.ios;

        if (!minimumVersion) {
          if (isMounted) {
            setState({ status: "allowed" });
          }
          return;
        }

        const currentVersion = appInfo.version || packageJson.version;
        const isOutdated =
          compareClientVersions(currentVersion, minimumVersion) < 0;

        if (!isMounted) {
          return;
        }

        setState(
          isOutdated
            ? {
                status: "blocked",
                appStoreUrl: config.appStoreUrl ?? DEFAULT_APP_STORE_URL,
                minimumVersion,
              }
            : { status: "allowed" },
        );
      } catch (error) {
        console.warn("Skipping app version check:", error);
      }
    };

    void checkVersion();
    const intervalId = window.setInterval(checkVersion, CHECK_INTERVAL_MS);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  if (state.status === "allowed") {
    return <>{children}</>;
  }

  if (state.status === "blocked") {
    return <ForceUpdateScreen appStoreUrl={state.appStoreUrl} />;
  }

  return <>{children}</>;
}

function ForceUpdateScreen({ appStoreUrl }: { appStoreUrl: string }) {
  return (
    <BlockingScreen
      title="Update required"
      description="A newer version of Saile is required to continue. Update from the App Store to keep using the app."
    >
      <ExternalLink
        className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-md bg-gray-900 px-5 py-3 text-base font-medium text-white transition-colors hover:bg-gray-800 sm:w-auto"
        href={appStoreUrl}
      >
        <AppleLogo className="h-5 w-5" fill="#fff" />
        <span>Update in App Store</span>
      </ExternalLink>
    </BlockingScreen>
  );
}

function BlockingScreen({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <main className="fixed inset-0 z-[100] flex min-h-dvh items-center justify-center bg-white px-6 text-center">
      <section className="flex w-full max-w-sm flex-col items-center gap-5">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
          <AppleLogo className="h-7 w-7" fill="#111827" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-gray-950">{title}</h1>
          <p className="text-base leading-6 text-gray-600">{description}</p>
        </div>
        {children}
      </section>
    </main>
  );
}
